
import { formatPrice } from '@/utils/serviceUtils';

interface ServicePricingProps {
  price: number;
  discountedPrice?: number | null;
  travelFee?: number | null;
  currency?: string | null;
  serviceMode?: string;
  variant?: 'compact' | 'detailed';
}

export const ServicePricing = ({
  price,
  discountedPrice,
  travelFee,
  currency,
  serviceMode,
  variant = 'compact'
}: ServicePricingProps) => {
  const hasDiscount = discountedPrice && discountedPrice > 0 && discountedPrice < price;
  const hasTravelFee = serviceMode === 'in_person' && travelFee && travelFee > 0;
  
  // Calculate total price
  const servicePrice = hasDiscount ? (discountedPrice || 0) : price;
  const totalPrice = hasTravelFee ? servicePrice + (travelFee || 0) : servicePrice;

  if (variant === 'compact') {
    return (
      <div className="text-right shrink-0">
        {/* Always show total price prominently */}
        <span className="font-bold text-gebeya-pink whitespace-nowrap">
          {formatPrice(totalPrice, null, currency)}
        </span>
        
        {/* Show breakdown details if needed */}
        {(hasDiscount || hasTravelFee) && (
          <div className="text-xs text-gray-600 mt-1">
            {hasDiscount && (
              <span className="line-through block">
                {formatPrice(price, null, currency)}
              </span>
            )}
            {hasTravelFee && (
              <span className="block">
                (Includes travel fee)
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  // Detailed view
  return (
    <div className="mb-4">
      {hasTravelFee ? (
        <div className="mt-2 flex flex-col space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Service fee:</span>
            <span className="font-medium">
              {hasDiscount 
                ? formatPrice(discountedPrice || 0, null, currency)
                : formatPrice(price, null, currency)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Travel fee:</span>
            <span className="font-medium">{formatPrice(travelFee || 0, null, currency)}</span>
          </div>
          <div className="flex justify-between text-sm pt-1 border-t border-gray-100">
            <span className="font-medium">Total:</span>
            <span className="font-bold text-gebeya-pink">
              {formatPrice(totalPrice, null, currency)}
            </span>
          </div>
        </div>
      ) : hasDiscount ? (
        <div className="flex items-center">
          <span className="text-lg text-gray-500 line-through mr-2">
            {formatPrice(price, null, currency)}
          </span>
          <span className="text-xl font-bold text-gebeya-pink">
            {formatPrice(discountedPrice || 0, null, currency)}
          </span>
          <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
            {Math.round((1 - ((discountedPrice || 0) / price)) * 100)}% OFF
          </span>
        </div>
      ) : (
        <span className="text-xl font-bold text-gebeya-pink">
          {formatPrice(price, null, currency)}
        </span>
      )}
    </div>
  );
};
